import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

const FundingOptions = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [stages, setStages] = useState([]);
  
  // Filters
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedSector, setSelectedSector] = useState(searchParams.get('sector') || '');
  const [selectedStage, setSelectedStage] = useState(searchParams.get('stage') || '');
  const [selectedGeography, setSelectedGeography] = useState(searchParams.get('geography') || '');
  const [minTicketSize, setMinTicketSize] = useState(searchParams.get('min') || '');
  const [maxTicketSize, setMaxTicketSize] = useState(searchParams.get('max') || '');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');

  useEffect(() => {
    fetchTaxonomy();
    fetchFunds();
  }, [selectedCategory, selectedSector, selectedStage, selectedGeography, minTicketSize, maxTicketSize, searchQuery]);

  const fetchTaxonomy = async () => {
    try {
      const [categoriesRes, sectorsRes, stagesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/funding-options/taxonomy/categories`),
        axios.get(`${API_BASE_URL}/api/funding-options/taxonomy/sectors`),
        axios.get(`${API_BASE_URL}/api/funding-options/taxonomy/stages`)
      ]);
      setCategories(categoriesRes.data.categories || []);
      setSectors(sectorsRes.data.sectors || []);
      setStages(stagesRes.data.stages || []);
    } catch (error) {
      console.error('Error fetching taxonomy:', error);
    }
  };

  const fetchFunds = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedSector) params.append('sector', selectedSector);
      if (selectedStage) params.append('stage', selectedStage);
      if (selectedGeography) params.append('geography', selectedGeography);
      if (minTicketSize) params.append('min_ticket_size', minTicketSize);
      if (maxTicketSize) params.append('max_ticket_size', maxTicketSize);
      if (searchQuery) params.append('search', searchQuery);

      const { data } = await axios.get(`${API_BASE_URL}/api/funding-options/?${params.toString()}`);
      setFunds(data);
    } catch (error) {
      console.error('Error fetching funds:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedSector('');
    setSelectedStage('');
    setSelectedGeography('');
    setMinTicketSize('');
    setMaxTicketSize('');
    setSearchQuery('');
    setSearchParams({});
  };

  const getCategoryBadgeColor = (category) => {
    const colors = {
      'Impact': 'bg-blue-100 text-blue-800',
      'Climate': 'bg-green-100 text-green-800',
      'Hybrid': 'bg-purple-100 text-purple-800',
      'Emerging': 'bg-orange-100 text-orange-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const formatTicketSize = (min, max) => {
    if (!min && !max) return 'Not specified';
    if (min && max) return `₹${min}L - ₹${max}L`;
    if (min) return `From ₹${min}L`;
    if (max) return `Up to ₹${max}L`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-16 pt-32">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Funding Options
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl">
            Discover impact investors, VC funds, and funding opportunities aligned with your social mission. 
            Apply through CSED for expert guidance and support.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <div className="lg:w-1/4">
            <Card className="p-6 sticky top-4">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold">Filters</h2>
                {(selectedCategory || selectedSector || selectedStage || selectedGeography || minTicketSize || maxTicketSize || searchQuery) && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-[#E87722] hover:underline"
                  >
                    Clear All
                  </button>
                )}
              </div>

              {/* Search */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Search</label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search funds..."
                  className="w-full border rounded-md p-2 text-sm"
                />
              </div>

              {/* Category Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full border rounded-md p-2 text-sm"
                >
                  <option value="">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Sector Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Sector</label>
                <select
                  value={selectedSector}
                  onChange={(e) => setSelectedSector(e.target.value)}
                  className="w-full border rounded-md p-2 text-sm"
                >
                  <option value="">All Sectors</option>
                  {sectors.map((sector) => (
                    <option key={sector} value={sector}>{sector}</option>
                  ))}
                </select>
              </div>

              {/* Stage Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Investment Stage</label>
                <select
                  value={selectedStage}
                  onChange={(e) => setSelectedStage(e.target.value)}
                  className="w-full border rounded-md p-2 text-sm"
                >
                  <option value="">All Stages</option>
                  {stages.map((stage) => (
                    <option key={stage} value={stage}>{stage}</option>
                  ))}
                </select>
              </div>

              {/* Geography Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Geography</label>
                <select
                  value={selectedGeography}
                  onChange={(e) => setSelectedGeography(e.target.value)}
                  className="w-full border rounded-md p-2 text-sm"
                >
                  <option value="">All Regions</option>
                  <option value="India">India</option>
                  <option value="South Asia">South Asia</option>
                  <option value="Global">Global</option>
                  <option value="Africa">Africa</option>
                  <option value="Latin America">Latin America</option>
                </select>
              </div>

              {/* Ticket Size Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Ticket Size (₹ Lakhs)</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={minTicketSize}
                    onChange={(e) => setMinTicketSize(e.target.value)}
                    placeholder="Min"
                    className="w-1/2 border rounded-md p-2 text-sm"
                  />
                  <input
                    type="number"
                    value={maxTicketSize}
                    onChange={(e) => setMaxTicketSize(e.target.value)}
                    placeholder="Max"
                    className="w-1/2 border rounded-md p-2 text-sm"
                  />
                </div>
              </div>
            </Card>
          </div>

          {/* Funds Grid */}
          <div className="lg:w-3/4">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold">
                {funds.length} {funds.length === 1 ? 'Fund' : 'Funds'} Available
              </h2>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E87722] mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading funding options...</p>
              </div>
            ) : funds.length === 0 ? (
              <Card className="p-12 text-center">
                <p className="text-gray-600 text-lg mb-4">No funding options found matching your criteria.</p>
                <Button onClick={clearFilters}>Clear Filters</Button>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {funds.map((fund) => (
                  <Card key={fund.id} className="p-6 hover:shadow-lg transition-shadow">
                    {/* Category Badge */}
                    <div className="mb-3">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getCategoryBadgeColor(fund.category)}`}>
                        {fund.category}
                      </span>
                    </div>

                    {/* Fund Name */}
                    <h3 className="text-xl font-bold mb-2">{fund.fund_name}</h3>

                    {/* Description */}
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {fund.description}
                    </p>

                    {/* Investment Details */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-start text-sm">
                        <span className="font-medium text-gray-700 w-24">Stage:</span>
                        <span className="text-gray-600">{fund.stage.join(', ')}</span>
                      </div>
                      <div className="flex items-start text-sm">
                        <span className="font-medium text-gray-700 w-24">Ticket Size:</span>
                        <span className="text-gray-600">{formatTicketSize(fund.ticket_size_min, fund.ticket_size_max)}</span>
                      </div>
                      <div className="flex items-start text-sm">
                        <span className="font-medium text-gray-700 w-24">Geography:</span>
                        <span className="text-gray-600">{fund.geography_focus.join(', ')}</span>
                      </div>
                    </div>

                    {/* Sectors Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {fund.key_sectors.slice(0, 3).map((sector, idx) => (
                        <span key={idx} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                          {sector}
                        </span>
                      ))}
                      {fund.key_sectors.length > 3 && (
                        <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                          +{fund.key_sectors.length - 3} more
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                      {!user ? (
                        // Not logged in - can click View Details but will redirect to login
                        <>
                          <Button 
                            onClick={() => navigate(`/login?redirect=/funding-options/${fund.id}`)}
                            variant="outline" 
                            className="flex-1"
                          >
                            View Details
                          </Button>
                          <Button 
                            onClick={() => navigate('/login')}
                            className="flex-1 bg-gray-400 cursor-not-allowed" 
                            disabled
                          >
                            Login to Apply
                          </Button>
                        </>
                      ) : user.has_active_subscription ? (
                        // Subscribed member - full access
                        <>
                          <Link
                            to={`/funding-options/${fund.id}`}
                            className="flex-1"
                          >
                            <Button variant="outline" className="w-full">
                              View Details
                            </Button>
                          </Link>
                          <Link
                            to={`/funding-options/${fund.id}?apply=true`}
                            className="flex-1"
                          >
                            <Button className="w-full bg-[#E87722] hover:bg-[#d16b1a]">
                              Apply
                            </Button>
                          </Link>
                        </>
                      ) : (
                        // Free logged-in user - can view but not apply
                        <>
                          <Link
                            to={`/funding-options/${fund.id}`}
                            className="flex-1"
                          >
                            <Button variant="outline" className="w-full">
                              View Details
                            </Button>
                          </Link>
                          <Link
                            to="/membership"
                            className="flex-1"
                          >
                            <Button className="w-full bg-[#E87722] hover:bg-[#d16b1a]">
                              Subscribe to Apply
                            </Button>
                          </Link>
                        </>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundingOptions;
